import { useEffect, useState, useMemo, useContext } from "react";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LInput from "@components/LInput";
import LPressable from "@components/LPressable";
import LIcon from "@components/LIcon";
import Accordion from "@components/Accordion";
import { AccordionContext } from "@components/Accordion/Context";
import { fonts, theme } from "@theme";
import { s as scaleFont } from "@utils/scale";

export function PayWhatYouWant({
  price,
  priceMax,
  bookingFee,
  onPriceChange,
}: {
  price: number | null;
  priceMax: number | null | undefined;
  bookingFee?: number | null;
  onPriceChange?: (price: number) => void;
}) {
  // Calculate booking fee and amounts following Entry.tsx pattern (lines 82-94)
  const minimumAmount = (price ?? 0) + (bookingFee ?? 0);
  const recommendedAmount = minimumAmount;

  // Calculate if this is a pay what you want ticket (lines 142-149)
  const isPayWhatYouWant = !!minimumAmount && !priceMax;

  // Calculate preset amounts for PWYW (lines 146-149)
  const payWhatYouWantOptions = useMemo(
    () => [
      recommendedAmount === 1 ? 3 : Math.floor(recommendedAmount * 1.2),
      recommendedAmount === 1 ? 5 : Math.floor(recommendedAmount * 1.7),
    ],
    [recommendedAmount],
  );

  // State management
  const [ticketPrice, setTicketPrice] = useState<number>(recommendedAmount);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [customAmount, setCustomAmount] = useState<number>(recommendedAmount);

  // Reset prices during render when the recommended amount changes (instead of
  // in an effect), so the inputs reflect the new recommendation immediately.
  const [prevRecommendedAmount, setPrevRecommendedAmount] =
    useState(recommendedAmount);
  if (recommendedAmount !== prevRecommendedAmount) {
    setPrevRecommendedAmount(recommendedAmount);
    setTicketPrice(recommendedAmount);
    setCustomAmount(recommendedAmount);
  }

  // Reset the custom amount back to the recommended value when an error first
  // appears, derived from the error transition rather than set in an effect.
  const [prevErrorMessage, setPrevErrorMessage] = useState(errorMessage);
  if (errorMessage !== prevErrorMessage) {
    setPrevErrorMessage(errorMessage);
    if (errorMessage) {
      setCustomAmount(recommendedAmount);
    }
  }

  const handleAmountChange = (value: string) => {
    const newAmount = Number(value);
    setCustomAmount(newAmount);
    setTicketPrice(newAmount);
    onPriceChange?.(newAmount);
    if (newAmount >= minimumAmount) {
      setErrorMessage("");
    }
  };

  const handleSetAmount = (amount: number) => {
    setTicketPrice(amount);
    onPriceChange?.(amount);
    if (amount >= minimumAmount) {
      setErrorMessage("");
    }
  };

  // Notify the parent of the recommended amount whenever it changes. This is a
  // sync to an external consumer (the parent callback), so it stays in an effect.
  useEffect(() => {
    onPriceChange?.(recommendedAmount);
  }, [recommendedAmount, onPriceChange]);

  // Don't render if not a PWYW ticket
  if (!isPayWhatYouWant) {
    return null;
  }

  return (
    <Accordion
      title="pay_what_you_want"
      showDivider={false}
      borderColor="whiteBorder"
      borderTopWidth={1}
      pt="s2"
      mt="s2"
    >
      <Accordion.Header showExpandIndicator={false}>
        <PayWhatYouWantHeader />
      </Accordion.Header>
      <Accordion.Content gap="s2" pt="s2">
        <LBox flexDirection="row" gap="s3" alignItems="stretch">
          {/* Input Field */}
          <LBox
            flex={1}
            borderColor={
              ticketPrice === customAmount ? "white" : "whiteTranslucent10"
            }
            borderWidth={1}
            borderRadius="m"
            flexDirection="row"
            height={32}
          >
            <LBox
              justifyContent="center"
              alignItems="center"
              borderRightWidth={1}
              borderColor="whiteTranslucent10"
              px="s2"
              mr="s0_5"
            >
              <LText variant="Body/B2/Medium" color="white">
                £
              </LText>
            </LBox>
            <LInput
              variant="priceInput"
              value={customAmount.toString()}
              onChangeText={handleAmountChange}
              onFocus={() => handleAmountChange(customAmount.toString())}
              keyboardType="numeric"
              style={{
                borderRadius: theme.borderRadii.m,
                flex: 1,
                width: "100%",
                backgroundColor: "transparent",
                color: "white",
                fontSize: scaleFont(14),
                fontFamily: fonts.medium,
              }}
            />
          </LBox>

          {payWhatYouWantOptions.map((presetAmount, index) => (
            <LBox flex={1} key={index}>
              <LPressable
                sentryEventName={`pay_what_you_want_${presetAmount}_selected`}
                onPress={() => handleSetAmount(presetAmount)}
              >
                <LBox
                  height={32}
                  borderRadius="m"
                  borderWidth={1}
                  borderColor="whiteTranslucent10"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={
                    presetAmount === ticketPrice ? "white" : "transparent"
                  }
                >
                  <LText
                    variant="Body/B2/Medium"
                    color={presetAmount === ticketPrice ? "black" : "white"}
                  >
                    £{presetAmount}
                  </LText>
                </LBox>
              </LPressable>
            </LBox>
          ))}
        </LBox>

        {errorMessage ? (
          <LText variant="bodyM2" color="danger">
            {errorMessage}
          </LText>
        ) : null}
      </Accordion.Content>
    </Accordion>
  );
}

function PayWhatYouWantHeader() {
  const { isOpen } = useContext(AccordionContext);

  return (
    <LBox flexDirection="row" alignItems="center" flex={1} gap="s1">
      <LText variant="Caption/C1/Regular">Pay what you can</LText>
      <LIcon name="down_arrow" rotate={isOpen ? 180 : 0} size={18} />
    </LBox>
  );
}
